'use client';

import React, { useState, useEffect } from 'react';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useNotificationCenter } from '@/providers/NotificationCenterProvider';
import { NotificationType, PreferenceUpdateItemTypeEnum, NotificationSettings } from '@/providers/NotificationCenterProvider';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';
import { ConfirmModal } from '@/components/ui/ConfirmModal';

interface NotificationCategory {
    name: string;
    types: Array<{
        type: NotificationType;
        label: string;
        description: string;
        isComingSoon?: boolean;
    }>;
}

const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
    {
        name: 'Order Updates',
        types: [
            {
                type: PreferenceUpdateItemTypeEnum.ORDERCONFIRMATION,
                label: 'Order Confirmed',
                description: 'Get notified when your order is confirmed',
            },
            {
                type: PreferenceUpdateItemTypeEnum.ORDERSHIPPED,
                label: 'Order Shipped',
                description: 'Track your order when it ships',
            },
            {
                type: PreferenceUpdateItemTypeEnum.ORDERDELIVERED,
                label: 'Order Delivered',
                description: 'Know when your order arrives',
            },
        ],
    },
    {
        name: 'Security',
        types: [
            {
                type: PreferenceUpdateItemTypeEnum.NEWDEVICELOGIN,
                label: 'New Device Login',
                description: 'Alert when logging in from a new device',
                isComingSoon: true,
            },
            {
                type: PreferenceUpdateItemTypeEnum.TWOFACODE,
                label: 'Two-Factor Authentication',
                description: 'Receive 2FA codes',
                isComingSoon: true,
            },
        ],
    },
    {
        name: 'Marketing',
        types: [
            {
                type: PreferenceUpdateItemTypeEnum.ABANDONEDCARTREMINDER,
                label: 'Abandoned Cart',
                description: 'Reminders for items left in cart',
                isComingSoon: true,
            },
            {
                type: PreferenceUpdateItemTypeEnum.PRICEDROPALERT,
                label: 'Price Drop Alert',
                description: 'Notify when prices drop on saved items',
                isComingSoon: true,
            },
            {
                type: PreferenceUpdateItemTypeEnum.BACKINSTOCK,
                label: 'Back in Stock',
                description: 'Alert when out-of-stock items return',
                isComingSoon: true,
            },
        ],
    },
];

// Custom Minimal Switch Component
const Switch = ({ checked, onChange, disabled }: { checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean }) => (
    <button
        onClick={() => !disabled && onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${checked ? 'bg-black' : 'bg-slate-200'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
        <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out ${checked ? 'translate-x-4' : 'translate-x-0'
                }`}
        />
    </button>
);

export function NotificationSettingsScreen() {
    const router = useRouter();
    const { buildPath } = useBasePath();
    const { settings, updateSettings, isLoading } = useNotificationCenter();
    const [localSettings, setLocalSettings] = useState(settings);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    useEffect(() => {
        if (settings) {
            setLocalSettings(settings);
        }
    }, [settings]);

    const getChannelSetting = (type: NotificationType, channel: 'email' | 'push' | 'inApp'): boolean => {
        const activeSettings = hasChanges ? localSettings : settings;
        if (!activeSettings?.preferences || !Array.isArray(activeSettings.preferences)) {
            return false;
        }

        const normalize = (s: any) => s?.toString().replace(/[^a-zA-Z0-9]/g, '').toUpperCase() || '';
        const targetType = normalize(type);

        const pref = activeSettings.preferences.find(p => normalize(p.type) === targetType);

        if (!pref) return false;

        const val = (pref.settings as any)?.[channel];
        return val === true || val === 'true' || val === 1 || val === '1';
    };

    const updateChannelSetting = (type: NotificationType, channel: 'email' | 'push' | 'inApp', value: boolean) => {
        setLocalSettings(prev => {
            const currentSettings = prev || settings || { preferences: [] };
            const currentPrefs = Array.isArray(currentSettings.preferences) ? currentSettings.preferences : [];
            const preferences = [...currentPrefs];

            const normalize = (s: any) => s?.toString().replace(/[^a-zA-Z0-9]/g, '').toUpperCase() || '';
            const targetType = normalize(type);

            const existingIndex = preferences.findIndex(p => normalize(p.type) === targetType);

            if (existingIndex >= 0) {
                preferences[existingIndex] = {
                    ...preferences[existingIndex],
                    settings: {
                        ...preferences[existingIndex].settings,
                        [channel]: value,
                    },
                };
            } else {
                preferences.push({
                    type,
                    settings: {
                        email: channel === 'email' ? value : true,
                        push: channel === 'push' ? value : true,
                        inApp: channel === 'inApp' ? value : true,
                    },
                });
            }

            return { ...currentSettings, preferences };
        });

        setHasChanges(true);
    };

    const handleSave = async () => {
        if (!localSettings) return;

        setIsSaving(true);
        try {
            await updateSettings(localSettings);
            setHasChanges(false);
        } catch (error) {
            console.error('Failed to save settings:', error);
            alert('Failed to save settings. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDisableAll = () => {
        const preferences: NotificationSettings['preferences'] = [];

        NOTIFICATION_CATEGORIES.forEach(category => {
            category.types.forEach(notifType => {
                preferences.push({
                    type: notifType.type,
                    settings: {
                        email: false,
                        push: false,
                        inApp: false,
                    },
                });
            });
        });

        setLocalSettings(prev => ({
            ...(prev || settings || { preferences: [] }),
            preferences
        }));
        setHasChanges(true);
        setIsConfirmModalOpen(false);
    };

    if (isLoading && !settings) {
        return (
            <div className="flex h-[50vh] flex-col items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-4xl px-6 py-8">

            {/* Header */}
            <div className="mb-6 flex items-end justify-between border-b border-slate-100 pb-4">
                <div>
                    <button
                        onClick={() => router.push(buildPath('/account'))}
                        className="mb-3 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Settings
                    </button>
                    <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Notifications</h1>
                    <p className="mt-2 text-sm text-slate-500">
                        Manage your notification preferences across all channels.
                    </p>
                </div>
            </div>

            {/* Notification Table Header */}
            <div className="hidden grid-cols-12 gap-4 border-b border-slate-100 pb-3 text-xs font-medium uppercase tracking-wider text-slate-400 sm:grid">
                <div className="col-span-8">Topic</div>
                <div className="col-span-2 text-center">Email</div>
                <div className="col-span-2 text-center">Push</div>
                {/* <div className="col-span-2 text-center">In-App</div> */}
            </div>

            {/* Content */}
            <div className="space-y-8 py-4">
                {NOTIFICATION_CATEGORIES.map((category) => (
                    <section key={category.name}>
                        <h2 className="mb-6 text-sm font-semibold text-slate-900">{category.name}</h2>

                        <div className="space-y-4">
                            {category.types.map((notifType) => (
                                <div key={notifType.type} className="group grid grid-cols-1 sm:grid-cols-12 gap-y-3 sm:gap-x-4 items-start sm:items-center">
                                    {/* Info */}
                                    <div className="sm:col-span-8 pr-4">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-sm font-medium ${notifType.isComingSoon ? 'text-slate-400' : 'text-slate-700'}`}>
                                                {notifType.label}
                                            </span>
                                            {notifType.isComingSoon && (
                                                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                                                    Soon
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-0.5 text-xs text-slate-500">{notifType.description}</p>
                                    </div>

                                    {/* Toggles */}
                                    <div className="sm:col-span-4 grid grid-cols-2 gap-2 sm:gap-4 bg-slate-50 sm:bg-transparent rounded-lg p-3 sm:p-0">
                                        <div className="flex items-center justify-start sm:justify-center gap-2">
                                            <span className="sm:hidden text-xs font-semibold text-slate-700 uppercase tracking-wide">Email</span>
                                            <Switch
                                                checked={getChannelSetting(notifType.type, 'email')}
                                                onChange={(checked) => updateChannelSetting(notifType.type, 'email', checked)}
                                                disabled={notifType.isComingSoon}
                                            />
                                        </div>
                                        <div className="flex items-center justify-start sm:justify-center gap-2">
                                            <span className="sm:hidden text-xs font-semibold text-slate-700 uppercase tracking-wide">Push</span>
                                            <Switch
                                                checked={getChannelSetting(notifType.type, 'push')}
                                                onChange={(checked) => updateChannelSetting(notifType.type, 'push', checked)}
                                                disabled={notifType.isComingSoon}
                                            />
                                        </div>
                                        {/* 
                                        <div className="flex items-center justify-between sm:justify-center col-span-1">
                                            <span className="sm:hidden text-xs text-slate-400">In-App</span>
                                            <Switch
                                                checked={getChannelSetting(notifType.type, 'inApp')}
                                                onChange={(checked) => updateChannelSetting(notifType.type, 'inApp', checked)}
                                                disabled={notifType.isComingSoon}
                                            />
                                        </div> 
                                        */}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                ))}
            </div>

            <div className="mt-12 border-t border-slate-100 pt-8 text-center sm:text-left">
                <Button
                    variant="ghost"
                    className="text-xs text-slate-400 hover:text-red-600 px-0"
                    onClick={() => setIsConfirmModalOpen(true)}
                >
                    Disable all notifications
                </Button>
            </div>

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleDisableAll}
                title="Disable All Notifications"
                message="Are you sure you want to disable all notifications? You can re-enable them individually later."
                confirmText="Disable All"
                cancelText="Cancel"
                variant="danger"
            />

            {hasChanges && (
                <div className="bg-white/80 backdrop-blur-sm fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-xl shadow-lg border border-slate-200 p-2 px-4 flex items-center gap-4 animate-in slide-in-from-bottom-2 fade-in duration-300">
                    <span className="text-xs font-medium text-slate-600">Unsaved changes</span>
                    <div className="h-4 w-px bg-slate-200" />
                    <Button
                        onClick={handleSave}
                        isLoading={isSaving}
                        disabled={isSaving}
                        variant="primary"
                        className="h-8 rounded-lg px-4 text-xs font-medium bg-black text-white bg-slate-800 hover:bg-slate-700"
                    >
                        Save
                    </Button>
                </div>
            )}
        </div>
    );
}
